import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { BusinessKnowledgeBase, SyncHoursFromGmbResponse } from '@/types'
import { api, isApiError } from '@/api/client'

interface BusinessKnowledgeBaseState {
  kb: BusinessKnowledgeBase | null
  loading: boolean
  error: string | null
}

interface BusinessKnowledgeBaseActions {
  fetchKB: () => Promise<void>
  /** Cancel the in-flight fetchKB() request, if any. Call from an unmount cleanup. */
  cancelFetchKB: () => void
  updateKB: (data: Partial<BusinessKnowledgeBase>) => Promise<void>
  syncHoursFromGmb: () => Promise<SyncHoursFromGmbResponse>
  clearError: () => void
}

type BusinessKnowledgeBaseStore = BusinessKnowledgeBaseState & BusinessKnowledgeBaseActions

const initialState: BusinessKnowledgeBaseState = {
  kb: null,
  loading: false,
  error: null,
}

// Module-level (not store state) — a fresh fetchKB() call, or an unmounting
// component, aborts whatever request is still in flight. Leaving and re-entering
// Settings › Business remounts the form, which must not leave an uncancelled GET
// behind; an aborted/superseded request never surfaces as the user-facing
// "Network error" string.
let inFlightController: AbortController | null = null

export const useBusinessKnowledgeBaseStore = create<BusinessKnowledgeBaseStore>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchKB: async () => {
        inFlightController?.abort()
        const controller = new AbortController()
        inFlightController = controller
        set({ loading: true, error: null })
        try {
          const response = await api.get<BusinessKnowledgeBase>('/business/knowledge-base', {
            signal: controller.signal,
          })
          set({ kb: response, loading: false })
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            // Self-initiated cancellation (unmount or a newer fetch superseding
            // this one) — never surface as an error, and never clobber the
            // loading/error state a newer in-flight fetch may have just set.
            return
          }
          const message = isApiError(error) ? error.message : 'Failed to fetch knowledge base'
          set({ error: message, loading: false })
        }
      },

      cancelFetchKB: () => {
        inFlightController?.abort()
      },

      updateKB: async (data: Partial<BusinessKnowledgeBase>) => {
        set({ loading: true, error: null })
        try {
          const response = await api.put<BusinessKnowledgeBase>('/business/knowledge-base', data)
          set({ kb: response, loading: false })
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to update knowledge base'
          set({ error: message, loading: false })
          throw error
        }
      },

      // The hours land on the server immediately; the caller applies the
      // returned hours to its local form state. `kb` is deliberately left
      // untouched so unsaved edits to other fields are not reset.
      syncHoursFromGmb: async () => {
        return api.post<SyncHoursFromGmbResponse>(
          '/business/knowledge-base/sync-hours-from-gmb',
          {}
        )
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    { name: 'BusinessKnowledgeBaseStore', enabled: import.meta.env.DEV }
  )
)
